import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "./primitives";
import { MovementGlyph } from "./Graphics";
import { FORMULA_CARDS } from "./content";

const EASE = [0.16, 1, 0.3, 1];

function Movement({ card, i }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      data-testid={`formula-card-${i}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 1, ease: EASE }}
      className="group relative grid grid-cols-1 gap-8 border-t border-ink/15 py-12 transition-colors duration-500 md:grid-cols-12 md:gap-10 md:py-16"
    >
      {/* Left — number, glyph, title */}
      <div className="md:col-span-5">
        <div className="flex items-start gap-6">
          <span className="font-serif text-lg text-champagne md:text-xl">{card.no}</span>
          <MovementGlyph
            index={i}
            hovered={hovered}
            className="h-12 w-12 shrink-0 text-forest md:h-14 md:w-14"
          />
        </div>
        <h3 className="mt-6 font-serif text-3xl leading-[1.05] text-ink transition-colors duration-500 group-hover:text-forest sm:text-4xl lg:text-5xl">
          {card.title}
        </h3>
        <p className="mt-5 max-w-[32ch] font-serif text-xl italic text-ink/60 md:text-2xl">
          {card.tagline}
        </p>
      </div>

      {/* Right — always-visible points */}
      <div className="md:col-span-7">
        <ul className="grid grid-cols-1 gap-x-10 sm:grid-cols-2">
          {card.points.map((p, pi) => (
            <motion.li
              key={pi}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ delay: 0.15 + pi * 0.07, duration: 0.6, ease: EASE }}
              className="flex items-start gap-4 border-b border-ink/10 py-4"
            >
              <span className="mt-1 font-serif text-sm text-copper">
                {String(pi + 1).padStart(2, "0")}
              </span>
              <span className="leading-relaxed text-ink/75">{p}</span>
            </motion.li>
          ))}
        </ul>
      </div>

      {/* hover accent */}
      <ArrowUpRight
        strokeWidth={1}
        className="absolute right-0 top-12 h-7 w-7 -translate-y-2 text-forest opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100 md:top-16"
      />
    </motion.div>
  );
}

export default function FormulaCards() {
  return (
    <section id="formula" data-testid="formula-cards-section" className="relative bg-warm py-28 md:py-40">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal className="mb-16 max-w-[26ch] md:mb-24">
          <Overline className="text-copper">The Three Movements</Overline>
          <h2 className="mt-6 font-serif text-4xl leading-[1.05] text-ink sm:text-6xl lg:text-7xl">
            A system, not a service.
          </h2>
        </Reveal>

        <div className="border-b border-ink/15">
          {FORMULA_CARDS.map((card, i) => (
            <Movement key={card.no} card={card} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
